import React from 'react';
import { Button, Card, CardContent, CardHeader, Flex, GaugeChart, Heading, LineChart, LoadingOrbit, Tab, TabList, TabPanel, Tabs, Text, chartThemes, cn, useTheme } from '@chatsdet/ui';
import { ChartLine } from '@phosphor-icons/react';
import { useQaRunHistory } from '../../../hooks/useQaRunHistory';
import { useApiErrorHistory } from '../../../hooks/useApiErrorHistory';

type AppScoreView = 'score' | 'totals' | 'api';

function formatDayLabel(dateKey: string) {
  const d = new Date(`${dateKey}T00:00:00Z`);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function last30Days() {
  const days: { dateKey: string; label: string }[] = [];
  const today = new Date();
  for (let i = 29; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const dateKey = d.toISOString().split('T')[0];
    days.push({ dateKey, label: formatDayLabel(dateKey) });
  }
  return days;
}

export function AppScoreCard({ className }: { className?: string }) {
  const { config } = useTheme();
  const chartColors = chartThemes[config.theme];
  const [view, setView] = React.useState<AppScoreView>('score');

  const qaRunHistoryQuery = useQaRunHistory(60);
  const qaRunHistory = qaRunHistoryQuery.data;
  const apiErrorHistoryQuery = useApiErrorHistory();
  const apiErrorHistory = apiErrorHistoryQuery.data;

  const series = React.useMemo(() => {
    const runs = (qaRunHistory ?? []).filter((r) => typeof r.startedAt === 'string' && r.startedAt.length > 0);
    const runsByDate = new Map<string, (typeof runs)[number]>();

    for (const run of runs) {
      const dateKey = new Date(run.startedAt!).toISOString().split('T')[0];
      const prev = runsByDate.get(dateKey);
      if (!prev || new Date(run.startedAt!).getTime() > new Date(prev.startedAt!).getTime()) {
        runsByDate.set(dateKey, run);
      }
    }

    return last30Days().map(({ dateKey, label }) => {
      const run = runsByDate.get(dateKey);
      return {
        date: label,
        passRate: run ? run.passRate : null,
        healthy: run ? run.healthyTests : null,
        unhealthy: run ? run.unhealthyTests : null,
        hasData: Boolean(run),
        _run: run ?? null,
      };
    });
  }, [qaRunHistory]);

  const latest = React.useMemo(() => {
    const runs = (qaRunHistory ?? []).filter((r) => typeof r.startedAt === 'string' && r.startedAt.length > 0);
    return runs.reduce<(typeof runs)[number] | null>((acc, run) => {
      if (!acc) return run;
      return new Date(run.startedAt!).getTime() > new Date(acc.startedAt!).getTime() ? run : acc;
    }, null);
  }, [qaRunHistory]);

  const apiSeries = React.useMemo(() => {
    const dailyStats = apiErrorHistory?.dailyStats ?? [];

    // Build a map for quick lookup by date
    const statsByDate = new Map(dailyStats.map((stat) => [stat.date, stat]));

    return last30Days().map(({ dateKey, label }) => {
      const stat = statsByDate.get(dateKey);
      const hasErrorData = stat !== undefined && stat.errorCount > 0;

      return {
        date: label,
        errorCount: hasErrorData ? stat.errorCount : null,
        errorRate: stat?.errorRate ?? null,
        hasData: stat !== undefined && (stat.errorCount > 0 || stat.totalTestCount > 0),
      };
    });
  }, [apiErrorHistory]);

  const latestApiHealth = React.useMemo(() => {
    if (!apiErrorHistory) return { successRate: 100, totalErrors: 0 };

    const last7DaysSet = new Set(
      Array.from({ length: 7 }, (_, i) => {
        const d = new Date();
        d.setDate(d.getDate() - i);
        return d.toISOString().split('T')[0];
      })
    );

    const recentStats = apiErrorHistory.dailyStats.filter((s) => last7DaysSet.has(s.date));
    const recentErrors = recentStats.reduce((sum, s) => sum + s.errorCount, 0);
    const recentTests = recentStats.reduce((sum, s) => sum + s.totalTestCount, 0);

    const successRate =
      recentTests > 0 ? Math.max(0, Math.min(100, Math.round(100 - (recentErrors / recentTests) * 100))) : 100;

    return { successRate, totalErrors: recentErrors };
  }, [apiErrorHistory]);

  const latestPassRate = latest?.passRate ?? 0;
  const latestHealthy = latest?.healthyTests ?? 0;
  const latestUnhealthy = latest?.unhealthyTests ?? 0;
  const latestTotal = latestHealthy + latestUnhealthy;
  const maxTotal = Math.max(0, ...series.map((r) => (Number(r.healthy) || 0) + (Number(r.unhealthy) || 0)));
  const normalizedTotalScore = maxTotal > 0 ? Math.round((latestTotal / maxTotal) * 100) : 0;

  const isLoading = qaRunHistoryQuery.isLoading || (view === 'api' && apiErrorHistoryQuery.isLoading);
  const isError = qaRunHistoryQuery.isError || (view === 'api' && apiErrorHistoryQuery.isError);

  if (isLoading) {
    return (
      <Card padding="lg" className={className}>
        <CardContent>
          <Flex align="center" justify="center" direction="column" gap="sm" className="py-6">
            <LoadingOrbit size="lg" />
            <Text size="sm" className="text-muted-foreground">
              Loading app score...
            </Text>
          </Flex>
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    const handleRefetch = () => {
      qaRunHistoryQuery.refetch();
      if (view === 'api') apiErrorHistoryQuery.refetch();
    };
    return (
      <Card padding="lg" className={className}>
        <CardContent>
          <Flex direction="column" gap="md" align="start">
            <div>
              <Heading level={4}>Unable to load app score</Heading>
              <Text size="sm" className="text-muted-foreground mt-1">
                We couldn&apos;t fetch the latest data. Try again in a moment.
              </Text>
            </div>
            <Button variant="outline" onPress={handleRefetch}>
              Retry
            </Button>
          </Flex>
        </CardContent>
      </Card>
    );
  }

  const isScoreView = view === 'score';
  const isTotalsView = view === 'totals';
  const isApiView = view === 'api';

  return (
    <Card padding="lg" className={cn(className)}>
      <CardHeader>
        <div className="relative">
          <div className="pr-48">
            <Flex align="center" gap="sm">
              <ChartLine size={20} />
              <Heading level={3}>App Score</Heading>
            </Flex>
          </div>

          <div className="absolute right-0 top-0">
            <Tabs
              selectedKey={view}
              onSelectionChange={(key) => setView(key as AppScoreView)}
            >
              <TabList aria-label="App score view">
                <Tab id="score">Score</Tab>
                <Tab id="totals">Totals</Tab>
                <Tab id="api">API</Tab>
              </TabList>
              <TabPanel id="score" className="hidden" />
              <TabPanel id="totals" className="hidden" />
              <TabPanel id="api" className="hidden" />
            </Tabs>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <div className="flex flex-col gap-6 lg:flex-row lg:items-stretch">
          <div className="min-w-0 flex-1">
            <LineChart
              height={220}
              data={isApiView ? apiSeries : series}
              xDataKey="date"
              lines={
                isScoreView
                  ? [{ dataKey: 'passRate', name: 'Score', color: chartColors.success }]
                  : isTotalsView
                    ? [
                        { dataKey: 'healthy', name: 'Healthy', color: chartColors.colors[5] },
                        { dataKey: 'unhealthy', name: 'Unhealthy', color: chartColors.colors[4] },
                      ]
                    : [
                        { dataKey: 'errorCount', name: 'Errors', color: chartColors.colors[4] },
                        { dataKey: 'errorRate', name: 'Error Rate', color: chartColors.colors[3] },
                      ]
              }
              showLegend={isApiView}
              showYAxis={false}
              connectNulls={true}
              gridOpacity={1}
              xAxisInterval={4}
            />
          </div>

          <div className="flex shrink-0 items-center justify-center self-center">
            {isScoreView ? (
              <GaugeChart
                score={latestPassRate}
                label="Score"
                size="md"
                strokeWidth={8}
                showScore={true}
                showLabel={true}
                labelClassName="mt-0.5"
              />
            ) : isTotalsView ? (
              <GaugeChart
                score={normalizedTotalScore}
                label="Tests"
                size="md"
                strokeWidth={8}
                showScore={true}
                showLabel={true}
                labelClassName="mt-0.5"
                displayValue={latestTotal}
                color={chartColors.colors[5]}
              />
            ) : (
              <GaugeChart
                score={latestApiHealth.successRate}
                label="API Health"
                size="md"
                strokeWidth={8}
                showScore={true}
                showLabel={true}
                labelClassName="mt-0.5"
              />
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
